'use client';
import React, { useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { Card, CardLocale, getCardDescription, getCardTerms, getCardRedeemInstructions, getCardTitle } from '../../types/card';
import { useTranslations, useLocale } from 'next-intl';
import { countryFlagSrc, getCountryName } from '../../helpers/countries';
import LightAccordion from '../ui/LightAccordion';
import type { FAQItem } from '@/src/utils/faqBuilder';
import { buildProductFaqItems } from '@/src/utils/faqBuilder';

const ProductReviews = dynamic(() => import('./ProductReviews').then(m => ({ default: m.ProductReviews })));
const ProductFAQ = dynamic(() => import('./ProductFAQ'));
const SimilarProducts = dynamic(() => import('./SimilarProducts').then(m => ({ default: m.SimilarProducts })));

import {
  ArticleIcon,
  AssignmentTurnedInIcon,
  GavelIcon,
  PublicIcon,
  ReviewsIcon,
  CreditCardIcon,
} from '../icons';
import pdStyles from './StyledComponents.module.css';
import PaymentMethodsGrid from '../PaymentMethodsGrid';

const linkifyText = (text: string): React.ReactNode => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0;
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>;
  });
};

const STEP_PATTERN = /^(\d+)[\.\)]\s+(.+)$/;

const renderRedeemInstructions = (text: string): React.ReactNode => {
  const lines = text.split(/\r?\n+/).map(l => l.trim()).filter(Boolean);
  const stepLines = lines.filter(l => STEP_PATTERN.test(l));

  if (lines.length >= 2 && stepLines.length / lines.length >= 0.5) {
    return (
      <ol className={pdStyles.stepList}>
        {lines.map((line, idx) => {
          const match = line.match(STEP_PATTERN);
          if (match) {
            return (
              <li key={idx} className={pdStyles.stepItem}>
                <span className={pdStyles.stepNumber}>{match[1]}</span>
                <div className={pdStyles.stepText}>{linkifyText(match[2])}</div>
              </li>
            );
          }
          return (
            <li key={idx} className={pdStyles.stepNote}>
              {linkifyText(line)}
            </li>
          );
        })}
      </ol>
    );
  }

  return <div className={pdStyles.description}>{linkifyText(text)}</div>;
};

interface ProductBelowFold2Props {
  card: Card;
  faqItems?: FAQItem[];
}

export const ProductBelowFold2: React.FC<ProductBelowFold2Props> = ({ card, faqItems }) => {
  const t = useTranslations('product');
  const locale = useLocale() as CardLocale;

  const [brandCardIds, setBrandCardIds] = useState<string[]>([]);
  const [brandCardsLoaded, setBrandCardsLoaded] = useState(false);

  const handleBrandCardsLoaded = useCallback((cardIds: string[]) => {
    setBrandCardIds(cardIds);
    setBrandCardsLoaded(true);
  }, []);

  const cardDescription = getCardDescription(card, locale);
  const cardTerms = getCardTerms(card, locale);
  const cardRedeemInstructions = getCardRedeemInstructions(card, locale);
  const hasCountries = !!(card?.countries && card.countries.length > 0);
  // ACEB-206: hide the whole FAQ card when there are no items left to show.
  const productFaqItems = faqItems || buildProductFaqItems(card, locale, t);

  return (
    <>
      <br/>

      <div className={pdStyles.sectionCard}>
        <div className={pdStyles.belowFoldPadding}>
          <LightAccordion
            id="payment-methods"
            title={
              <h4 className={pdStyles.detailTitle} style={{ margin: 0 }}>
                <CreditCardIcon fontSize="small" />
                {t('sections.paymentMethods')}
              </h4>
            }
            defaultExpanded={true}
          >
            <PaymentMethodsGrid />
          </LightAccordion>
        </div>
      </div>

      {hasCountries && (
        <div className={pdStyles.sectionCard}>
          <div className={pdStyles.belowFoldPadding}>
            <LightAccordion
              id="countries"
              title={
                <h4 className={pdStyles.detailTitle} style={{ margin: 0 }}>
                  <PublicIcon fontSize="small" />
                  {t('sections.availableCountries')} ({card.countries!.length})
                </h4>
              }
              defaultExpanded={true}
            >
              <div className={pdStyles.description}>
                <div className={pdStyles.countryChipsContainer}>
                  {card.countries!.map((country, index) => {
                    const countryCode = country?.toUpperCase();
                    const countryName = getCountryName(countryCode);
                    return (
                      <div key={`country-${countryCode}-${index}`} className={pdStyles.countryChip}>
                        <span>{countryName}</span>
                        {countryCode === 'WW' ? (
                          <span style={{ fontSize: '16px', lineHeight: 1 }}>🌍</span>
                        ) : (
                          <img
                            src={countryFlagSrc(countryCode)}
                            alt={countryName}
                            width={18}
                            height={14}
                            style={{ width: '18px', height: '14px', borderRadius: '2px' }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </LightAccordion>
          </div>
        </div>
      )}

      {cardDescription && (
        <div className={pdStyles.sectionCard}>
          <div className={pdStyles.belowFoldPadding}>
            <LightAccordion
              id="description"
              title={
                <h4 className={pdStyles.detailTitle} style={{ margin: 0 }}>
                  <ArticleIcon fontSize="small" />
                  {t('sections.productDescription')}
                </h4>
              }
              defaultExpanded={true}
            >
              <div
                className={`${pdStyles.description} ${pdStyles.descriptionHtml}`}
                dangerouslySetInnerHTML={{ __html: cardDescription }}
              />
            </LightAccordion>
          </div>
        </div>
      )}

      {cardRedeemInstructions && (
        <div className={pdStyles.sectionCard}>
          <div className={pdStyles.belowFoldPadding}>
            <LightAccordion
              id="redeem"
              title={
                <h4 className={pdStyles.detailTitle} style={{ margin: 0 }}>
                  <AssignmentTurnedInIcon fontSize="small" />
                  {t('sections.howToUse')}
                </h4>
              }
            >
              {renderRedeemInstructions(cardRedeemInstructions)}
            </LightAccordion>
          </div>
        </div>
      )}

      {cardTerms && (
        <div className={pdStyles.sectionCard}>
          <div className={pdStyles.belowFoldPadding}>
            <LightAccordion
              id="terms"
              title={
                <h4 className={pdStyles.detailTitle} style={{ margin: 0 }}>
                  <GavelIcon fontSize="small" />
                  {t('sections.terms')}
                </h4>
              }
            >
              <div className={pdStyles.description}>{cardTerms}</div>
            </LightAccordion>
          </div>
        </div>
      )}

      <div className={pdStyles.sectionCard}>
        <div className={pdStyles.belowFoldPadding}>
          <LightAccordion
            id="customer-reviews"
            lazy
            title={
              <h4 className={pdStyles.detailTitle} style={{ margin: 0 }}>
                <ReviewsIcon fontSize="small" />
                {t('reviews.ratingsTitle', { title: getCardTitle(card, locale), count: card?.rating?.count || 0 })}
              </h4>
            }
          >
            <ProductReviews cardId={card._id} reviewScore={card?.rating?.score} reviewCount={card?.rating?.count} />
          </LightAccordion>
        </div>
      </div>

      {productFaqItems.length > 0 && (
        <div className={pdStyles.sectionCard}>
          <div className={pdStyles.belowFoldPadding}>
            <ProductFAQ card={card} faqItems={productFaqItems} />
          </div>
        </div>
      )}

      <br/>

      {(!brandCardsLoaded || brandCardIds.length > 0) && (
        <>
          <div className={pdStyles.cardWrapper}>
            <div className={pdStyles.belowFoldPadding}>
              <SimilarProducts
                currentCardId={card._id}
                title={t('similarProducts.sameBrandGiftCards')}
                fetchByBrand={true}
                onCardsLoaded={handleBrandCardsLoaded}
              />
            </div>
          </div>
          <br/>
        </>
      )}

      <div className={pdStyles.cardWrapper}>
        <div className={pdStyles.belowFoldPadding}>
          <SimilarProducts
            categories={card.category}
            currentCardId={card._id}
            title={t('similarProducts.similarGiftCards')}
            excludeCardIds={brandCardIds}
          />
        </div>
      </div>
    </>
  );
};

export default ProductBelowFold2;
